/*
Used on mobile/touch devices to close the drawer by dragging it down (in PDP, Featured images instagram section,...)

- The liquid variables (drawer_group_classes and backdrop_group_classes) used in the code snippet below have to be changed according to the Drawer being developed.
- Note that the toggle-element-class is also used here because of the Desktop drawer and Backdrop functionality.

HTML Usage:
<draggable-drawer
    class="invisible fixed bottom-0 left-0 right-0 top-auto z-modal w-full overflow-y-auto rounded-t-lg bg-color-default text-color-default shadow-drawer transition-all duration-500 max-md:max-h-[90%] max-md:translate-y-full max-md:group-[.drawer-dragging]/body:overflow-y-hidden max-md:group-[.drawer-dragging]/body:transition-none md:left-auto md:top-0 md:h-full md:max-w-[375px] md:translate-x-full md:rounded-none [@media(min-height:460px)]:max-md:max-h-[26.25rem] {{ drawer_group_classes }}"
    data-draggable-drawer
  >
    {%- comment -%} Close button {%- endcomment -%}
    <toggle-element-class
      class="sticky left-[calc(100%-3.5rem)] top-4 z-10 mr-4 flex w-10 justify-end max-md:[html.touch_&]:hidden"
      data-options='
        {
          "toggleClass": "{{ drawer_toggle_class }}"
        }
      '
    >
      <button
        type="button"
        class="button button--link border-none bg-accent-100 p-2 states:bg-accent-300 states:text-color-default"
        aria-label="{% render "format-translation", namespace: "accessibility", key: "close", fallback: "Close" %}"
      >
        {%- render 'global-icon', icon: 'close', icon_class: 'size-6' -%}
      </button>
    </toggle-element-class>

    {%- comment -%} Sticky Gray bar & Top shadow - Mobile {%- endcomment -%}
    <div class="sticky top-0 z-popover">
      {%- comment -%} bar to indicate the drawer is draggable {%- endcomment -%}
      <div
        class="flex w-full select-none items-center justify-center bg-white py-4 before:h-[.1875rem] before:w-14 before:rounded before:bg-offblack md:hidden max-md:[html.no-touch_&]:hidden"
        data-mobile-drag-bar
      ></div>

      {%- comment -%} shadow element to indicate there's more content above {%- endcomment -%}
      <div
        class="pointer-events-none h-0 opacity-0 transition-opacity before:absolute before:h-10 before:w-full before:bg-gradient-to-b before:from-white before:to-transparent before:content-empty"
        data-mobile-top-shadow
      ></div>
    </div>

    {%- comment -%} Drawer content {%- endcomment -%}
    <div class="mx-4 mb-4 block md:mt-4">Lorem ipsum</div>

    {%- comment -%} Bottom Shadow element (indication that there's more content below) {%- endcomment -%}
    <span
      class="pointer-events-none sticky bottom-0 left-0 right-0 z-popover block h-[4.5rem] bg-gradient-to-t from-white to-transparent"
    ></span>
  </draggable-drawer>

  {%- comment -%} Drawers backdrop {%- endcomment -%}
  <toggle-element-class
    role="button"
    aria-expanded
    aria-controls
    class="backdrop z-offcanvas-backdrop opacity-0 {{ backdrop_group_classes }}"
    aria-label="{% render 'format-translation', namespace: 'accessibility', key: 'close', fallback: 'Close' %}"
    data-options='
      {
        "toggleClass": "{{ drawer_toggle_class }}"
      }
    '
  >
  </toggle-element-class>

*/

if (!customElements.get('draggable-drawer')) {
  class draggableDrawer extends HTMLElement {
    constructor() {
      super();

      // get drag event to close drawer + show/hide top shadow according to the visibility of the top element / mobile or touch only
      if (!!('ontouchstart' in document)) {
        // get all the drag bars
        let mobileDragBars = this.querySelectorAll('[data-mobile-drag-bar]');

        mobileDragBars.forEach((dragBar) => {
          // Add the drag to close functionality
          dragBar.draggable = 'true';
          dragBar.addEventListener('touchstart', (event) => this.handleDrawerDragToClose(event));
          dragBar.addEventListener('mousedown', (event) => this.handleDrawerDragToClose(event));

          // Update the top shadow visibility
          this.updateTopShadowVisibility(dragBar);
        });
      }
    }

    /**
     * handleDrawerDragToClose
     * @description Handle drawer drag down to close it on mobile / mobile or touch only
     */
    handleDrawerDragToClose(event) {
      const drawer = this;

      // get the classes to open and close the drawer
      const body = document.querySelector('body');
      const { toggleClass, removeToggleClassesThatStartWith } = JSON.parse(
        drawer.querySelector('toggle-element-class').dataset.options
      );

      // Global variables for tracking drag events - Sets initial drag position, drawer height and sets transition to none for the drawer on dragging
      let isDragging = true;
      let startY = event.touches?.[0].clientY || event.clientY;
      const startHeight = drawer.offsetHeight;
      body.classList.add('drawer-dragging');

      // Calculates the new height for the drawer and calls the updateDrawerHeight function
      const dragging = (event) => {
        if (!isDragging) return;
        const difference = startY - (event.touches?.[0].clientY || event.clientY);
        const newHeight = startHeight + (difference / window.innerHeight) * 100;
        updateDrawerHeight(newHeight);
      };

      // Determines whether to hide or set the height to default
      const dragStop = () => {
        isDragging = false;
        body.classList.remove('drawer-dragging');
        const drawerHeight = drawer.offsetHeight;
        drawerHeight < 560 ? hideDrawer() : updateDrawerHeight(startHeight);

        // remove event listeners
        document.removeEventListener('mousemove', dragging);
        document.removeEventListener('mouseup', dragStop);
        document.removeEventListener('touchmove', dragging);
        document.removeEventListener('touchend', dragStop);
      };

      // updates the height of the drawer
      const updateDrawerHeight = (height) => (drawer.style.height = `${Math.floor(height)}px`);

      // Hide the drawer by removing the toggle element classes from the body
      const hideDrawer = () => {
        drawer.style.height = 'auto';

        // remove the main drawer type class
        body.classList.remove(toggleClass);

        // remove the specific drawer toggle class that starts with the given string
        if (removeToggleClassesThatStartWith) {
          let classToBeRemoved = new RegExp(String.raw`${removeToggleClassesThatStartWith}(\d)*`);
          for (let classCount = 0; classCount < body.classList.length; classCount++) {
            if (body.classList[classCount].match(classToBeRemoved)) {
              body.classList.remove(body.classList[classCount]);
            }
          }
        }
      };

      // add event listeners on drag (touch and mouse events)
      document.addEventListener('touchmove', dragging);
      document.addEventListener('touchend', dragStop);
      document.addEventListener('mousemove', dragging);
      document.addEventListener('mouseup', dragStop);
    }

    /**
     * updateTopShadowVisibility
     * @description Update the top shadow visibility according to where the user is in the drawer. Options & Upsell products drawers / mobile only, when the browser supports touch
     */
    updateTopShadowVisibility(dragBar) {
      const topShadow = dragBar.nextElementSibling.hasAttribute('data-mobile-top-shadow')
        ? dragBar.nextElementSibling
        : null;
      if (!topShadow) return;

      // show/hide the top shadow according to the visibility of the respective top element (like the fieldset legend) inside the drawer
      // threshold below 1 because WebKit only reports isIntersecting at ratio 1, which sub-pixel rounding can prevent
      const observerLegendCallback = (entries) =>
        entries.forEach((entry) => {
          // ignore hidden top elements (display: none), e.g. the ones of other options/drawer types
          if (!entry.boundingClientRect.height) return;
          topShadow.classList.toggle('opacity-0', entry.intersectionRatio >= 0.9);
        });
      const observerLegend = new IntersectionObserver(observerLegendCallback, { root: this, threshold: [0, 0.9] });

      // add an observer for the element on top of the drawer
      const topElements = this.querySelectorAll('[data-draggable-drawer-top-element]');
      topElements.forEach((topElement) => observerLegend.observe(topElement));
    }
  }

  customElements.define('draggable-drawer', draggableDrawer);
}
